import type { LaunchContext } from "../boot/launch/LaunchContextTypes";
import type { GameInitialSettings } from "../boot/settings/GameInitialSettingsTypes";
import { readQueryParam } from "../boot/utils/readQueryParam";

/**
 * Inputs the resolver considers. Missing fields are treated as absent.
 */
export interface OperatorIdResolverInputs {
  /** The build config's override (`BootConfig.operatorIdOverride`), like its `forcedVisualProfile` and `localeCodeOverride`. */
  configOverride?: string | null;

  /** Resolved launch context (URL query params may have populated operatorId upstream). */
  launchContext?: LaunchContext | null;

  /** Server-side initial settings — may carry `meta.operatorId`. */
  initialSettings?: GameInitialSettings | null;

  /**
   * Raw query string (e.g. `window.location.search`). Optional — injected to keep the
   * resolver testable; in production, `bootGame` passes the real value.
   */
  queryString?: string | null;
}

/**
 * Resolves the active operatorId from the first non-empty source, in priority order:
 *
 *   1. URL query param `?operator=<id>`
 *   2. `launchContext.operatorId`
 *   3. `initialSettings.meta.operatorId`
 *   4. The build config's override (`BootConfig.operatorIdOverride`)
 *
 * Returns `null` if none provide a value — default (unbranded) build runs.
 */
export function resolveOperatorId(inputs: OperatorIdResolverInputs): string | null {
  const fromQuery = readQueryParam(inputs.queryString, "operator");
  if (fromQuery) return fromQuery;

  const fromLaunch = normalize(inputs.launchContext?.operatorId);
  if (fromLaunch) return fromLaunch;

  const metaValue = inputs.initialSettings?.meta?.operatorId;
  const fromMeta = typeof metaValue === "string" ? normalize(metaValue) : null;
  if (fromMeta) return fromMeta;

  const fromOverride = normalize(inputs.configOverride);
  if (fromOverride) return fromOverride;

  return null;
}

function normalize(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

